import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import AppInput from 'components/Input/AppInput';
import AppCheckbox from 'components/Input/AppCheckbox';
import SelectField from 'components/Select/SelectField';
import AppButton from 'components/CustomButtons/AppButton';
import AppColor from "utilities/AppColor";
import { QUESTION_TYPE } from "config/AppConfig";

const useStyles = makeStyles(() => ({
  qFormTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },
  qFieldLabel: {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 4,
  },
  qFieldHint: {
    fontSize: 11,
    color: "#6B7280",
    marginTop: 4,
  },
  qFormActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
  btnPrimary: {
    background: AppColor.mainColor,
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    "&:hover": { background: AppColor.mainDarkColor || "#0056b3" },
    "&:disabled": { opacity: 0.7, cursor: "not-allowed" },
  },
  btnSecondary: {
    background: "#fff",
    color: "#374151",
    border: "1px solid #D1D5DB",
    padding: "6px 12px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": { background: "#F9FAFB" },
  },
}));

const NoteQuestionForm = ({ initialData, onSubmit, onCancel, saving, title, submitLabel, isEditMode }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const formattedInitialData = {
    ...initialData,
    quickAnswers: Array.isArray(initialData?.quickAnswers) ? initialData.quickAnswers : [],
  };

  return (
    <Box style={{ padding: "12px 14px", width: "100%" }}>
      <Box className={classes.qFormTitle} style={{ marginBottom: 8 }}>{title}</Box>
      <Formik
        initialValues={formattedInitialData}
        validationSchema={Yup.object({
          question: Yup.string()
            .required(t('validation.required'))
            .max(1000, t('validation.max_length', { value: 1000 })),
          contextHint: Yup.string()
            .max(255, t('validation.max_length_255')),
          quickAnswers: Yup.array().when('questionType', (questionType, schema) => {
            return questionType !== QUESTION_TYPE.YES_NO
              ? schema
                  .max(10, t('validation.max_items', { value: 10 }) || "Maximum 10 quick answers allowed")
                  .of(
                    Yup.string()
                      .trim()
                      .required(t('validation.required'))
                      .max(255, t('validation.max_length', { value: 255 }))
                  )
              : Yup.array().nullable().notRequired();
          }),
          detailPlaceholder: Yup.string()
            .max(255, t('validation.max_length', { value: 255 }))
        })}
        onSubmit={(values) => onSubmit(values)}
      >
        {({ values }) => {
          return (
            <Form>
              <div className="row">
                <div className="col-sm-12 col-md-12">
                  <Field component={AppInput} type="textarea" name="question" required label={t('label.question')} placeholder={t('text.questionExample')} />
                </div>
                <div className="col-sm-12 col-md-6">
                  <Field 
                    component={SelectField} 
                    name="questionType" 
                    label="Question Type" 
                    disabled={isEditMode}
                    options={[
                      { value: QUESTION_TYPE.DEFAULT, label: t('label.default') },
                      { value: QUESTION_TYPE.YES_NO, label: t('label.yes_no') }
                    ]}
                    style={{ marginBottom: 12 }} 
                  />
                </div>
                {values.questionType !== QUESTION_TYPE.YES_NO && (
                  <>
                    <div className="col-sm-12 col-md-6" style={{ marginTop: 25, alignContent: "center" }}>
                      <Field component={AppCheckbox} name="hasDetail" label="Allow workers to add a detail note below the chips" />
                    </div>
                    <div className="col-sm-12 col-md-12" style={{ marginBottom: 12 }}>
                      <span className={classes.qFieldLabel}>Quick answer options</span>
                      <FieldArray name="quickAnswers">
                        {({ push, remove }) => (
                          <Box>
                            {(values.quickAnswers || []).map((_, index) => (
                              <Box key={index} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                                <Box flex={1}>
                                  <Field
                                    component={AppInput}
                                    name={`quickAnswers.${index}`}
                                    placeholder={`Option ${index + 1}`}
                                  />
                                </Box>
                                <Box style={{ height: 40, display: "flex", alignItems: "center" }}>
                                  <IconButton onClick={() => remove(index)} color="secondary" style={{ padding: 8 }}>
                                    <i className="fas fa-trash-alt" style={{ fontSize: 16 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))}
                            {(values.quickAnswers || []).length < 10 && (
                              <AppButton
                                label={t("button.add_option", "Add option")}
                                mode="outlined"
                                buttonColor={AppColor.mainColor}
                                onClick={() => push("")}
                                startIcon={<i className="fas fa-plus" />}
                                style={{ marginTop: 4 }}
                              />
                            )}
                          </Box>
                        )}
                      </FieldArray>
                      <Box className={classes.qFieldHint} style={{ marginTop: 6 }}>
                        Workers tap these options to answer (maximum 10 options, max 255 characters each).
                      </Box>
                    </div>
                    <div className="col-sm-12 col-md-12">
                      <Field component={AppInput} name="contextHint" label="Context hint (optional)" placeholder="e.g. Check the communication book first" />
                    </div>
                    {values.hasDetail && (
                      <div className="col-sm-12 col-md-12" style={{ marginTop: 8 }}>
                        <Field component={AppInput} label={t('label.detail_placeholder')} name="detailPlaceholder" placeholder="Detail placeholder text…" />
                      </div>
                    )}
                  </>
                )}
              </div>
              <Box className={classes.qFormActions} mt={2}>
                <button type="button" className={classes.btnSecondary} onClick={onCancel} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className={classes.btnPrimary} disabled={saving || !values.question.trim()}>
                  {saving ? "Saving…" : submitLabel}
                </button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default NoteQuestionForm;
